import { ApiConfig } from '@headless-workspace/config';
import {
    Repository,
    Result,
    ResultFailure,
    ResultFailureFromApiError,
    ResultSuccess,
} from '@headless-workspace/core-domain';
import { initRequestStrategy } from '@headless-workspace/data';
import { UserRecommendationsParamsDTO } from './UserRecommendationsParamsDTO';
import { UserRecommendationsResponse } from './UserRecommendationsResponse';
import { UserRecommendationsValue } from './UserRecommendationsValue';

export const USER_RECOMMENDATIONS_ERROR_CODE = 'USER_RECOMMENDATIONS_ERROR';
export const USER_RECOMMENDATIONS_ERROR_MESSAGE = 'Failed to retrieve user recommendations';

export interface UserRecommendationsRepositorySpec {
    getRecommendations({
        locale,
        pageId,
        productId,
    }: UserRecommendationsParamsDTO): Promise<Result<UserRecommendationsValue>>;
}

export class UserRecommendationsRepository extends Repository implements UserRecommendationsRepositorySpec {
    path = ApiConfig.headless.paths.user;

    constructor(requestStrategy = initRequestStrategy()) {
        super(
            requestStrategy.authStrategy,
            requestStrategy.refreshTokenStrategy,
            requestStrategy.queryParamStrategy,
            ApiConfig.headless.basePath,
        );
    }

    async getRecommendations({
        locale,
        pageId,
        productId,
    }: UserRecommendationsParamsDTO): Promise<Result<UserRecommendationsValue>> {
        try {
            const url = `${this.path}/${ApiConfig.headless.endpoints.recommendations}`;

            const params = productId ? { locale, pageId, productId } : { locale, pageId };

            const response = await this.datasource.getResource<UserRecommendationsResponse>(url, params);

            if (!response || 'errorCode' in response) {
                const errors = response ? response.errors : undefined;
                return ResultFailure(
                    response?.errorCode ?? USER_RECOMMENDATIONS_ERROR_CODE,
                    USER_RECOMMENDATIONS_ERROR_MESSAGE,
                    errors,
                );
            }

            // No need to parse with a Decoder as API route already do the parsing
            return ResultSuccess(response.recommendations);
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }
}
